import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe } from '@jsverse/transloco';
import { IbanPipe, NotEmptyPipe } from '@zero/onboarding/domain';
import { IconComponent, InputComponent, NotificationComponent } from '@zero/shared/ui-web-design-system';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, firstValueFrom, map, tap } from 'rxjs';
import { BaseComponent } from '../../components/base.component';
import { OnboardingContainerComponent } from '../../components/onboarding-container/onboarding-container.component';

@Component({
  imports: [
    OnboardingContainerComponent,
    TranslocoPipe,
    InputComponent,
    AsyncPipe,
    IbanPipe,
    NotificationComponent,
    IconComponent,
    NotEmptyPipe,
  ],
  templateUrl: './bank-account.component.html',
  styleUrl: './bank-account.component.scss',
})
export class BankAccountComponent extends BaseComponent implements OnInit {
  readonly #name = new BehaviorSubject<string>(null);
  readonly #iban = new BehaviorSubject<string>(null);
  readonly #ibanError = new BehaviorSubject<string>(null);
  readonly #destroyRef = inject(DestroyRef);

  public readonly name$ = this.#name.asObservable();
  public readonly iban$ = this.#iban.asObservable();
  public readonly isFormValid$ = combineLatest([this.name$, this.iban$, this.store.sendingBankAccountInformation$, this.store.error$]).pipe(
    map(
      ([name, iban, sending, error]) => new NotEmptyPipe().transform(name) && iban && new IbanPipe().transform(iban) && !sending && !error,
    ),
  );
  public readonly ibanError$ = this.#ibanError.asObservable();

  override ngOnInit(): void {
    super.ngOnInit();
    this.#listenToValidIban();
  }

  public handleIbanBlur = (value: string) => {
    const isValid = new IbanPipe().transform(value);
    this.#ibanError.next(isValid ? null : 'onboarding.bank-account.error');
  };

  public handleIbanChange = (value: string) => {
    this.#iban.next(value ? value.toUpperCase() : '');
    this.#ibanError.next(null);
  };
  public handleNameChange = (value: string) => {
    this.#name.next(value);
  };
  public copy = () => {
    const cbd = this.customerStateFacade.customerBaseData;
    this.handleIbanChange(cbd.refAccIban);
    this.handleNameChange(`${cbd.firstName} ${cbd.lastName}`);
  };

  protected override populateFromData(): void {
    this.store.selectedChildOnboarding$
      .pipe(
        filter((data) => !!data),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(({ iban, kontoinhaber }) => {
        this.handleIbanChange(iban);
        this.handleNameChange(kontoinhaber);
      });
  }

  protected override listenToSuccessfulSubmit(): void {
    this.store.sentBankAccountInformationSuccessfully$
      .pipe(
        filter((sent) => sent),
        tap(() => this.store.clearBankData()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.service.goToNextStep());
  }

  protected override async submit(): Promise<void> {
    (await firstValueFrom(this.isFormValid$)) &&
      this.store.sendBankAccountInformation({
        iban: this.#iban.value,
        bankAccountOwner: this.#name.value,
      });
  }

  #listenToValidIban() {
    this.iban$
      .pipe(
        filter((iban) => !!iban),
        tap(() => this.store.clearBankData()),
        filter((iban) => new IbanPipe().transform(iban)),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((iban) => this.store.loadBankByIban(iban));
  }
}
